/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type PropsWithChildren,
} from 'react';

type ThemeMode = 'light' | 'dark' | 'system';
type ThemeVariableMap = Record<string, string>;
type ThemeState = {
  darkVariables: ThemeVariableMap;
  lightVariables: ThemeVariableMap;
  mode: ThemeMode;
  radius: string;
  surfaceRadius: string;
};

type ThemeCustomizerContextValue = {
  currentVariables: ThemeVariableMap;
  effectiveMode: Exclude<ThemeMode, 'system'>;
  mode: ThemeMode;
  radius: string;
  surfaceRadius: string;
  resetTheme: () => void;
  setMode: (mode: ThemeMode) => void;
  setRadius: (radius: string) => void;
  setSurfaceRadius: (radius: string) => void;
  setVariable: (name: string, value: string) => void;
};

const STORAGE_KEY = 'vibecode-theme-customizer';

const themeVariableNames = [
  'background',
  'foreground',
  'card',
  'card-foreground',
  'popover',
  'popover-foreground',
  'primary',
  'primary-foreground',
  'secondary',
  'secondary-foreground',
  'muted',
  'muted-foreground',
  'accent',
  'accent-foreground',
  'destructive',
  'border',
  'input',
  'ring',
  'chart-1',
  'chart-2',
  'chart-3',
  'chart-4',
  'chart-5',
  'sidebar',
  'sidebar-foreground',
  'sidebar-primary',
  'sidebar-primary-foreground',
  'sidebar-accent',
  'sidebar-accent-foreground',
  'sidebar-border',
  'sidebar-ring',
] as const;

export const themeVariableSections = [
  {
    description: 'Tokens structurants du canvas et des surfaces.',
    title: 'Base',
    variables: [
      { label: 'Background', name: 'background' },
      { label: 'Foreground', name: 'foreground' },
      { label: 'Card', name: 'card' },
      { label: 'Card foreground', name: 'card-foreground' },
      { label: 'Popover', name: 'popover' },
      { label: 'Popover foreground', name: 'popover-foreground' },
    ],
  },
  {
    description: 'Couleurs d action et d accentuation.',
    title: 'Brand',
    variables: [
      { label: 'Primary', name: 'primary' },
      { label: 'Primary foreground', name: 'primary-foreground' },
      { label: 'Secondary', name: 'secondary' },
      { label: 'Secondary foreground', name: 'secondary-foreground' },
      { label: 'Accent', name: 'accent' },
      { label: 'Accent foreground', name: 'accent-foreground' },
      { label: 'Destructive', name: 'destructive' },
      { label: 'Ring', name: 'ring' },
    ],
  },
  {
    description: 'Bords, inputs et couches neutres.',
    title: 'Surfaces',
    variables: [
      { label: 'Muted', name: 'muted' },
      { label: 'Muted foreground', name: 'muted-foreground' },
      { label: 'Border', name: 'border' },
      { label: 'Input', name: 'input' },
    ],
  },
  {
    description: 'Jeu de couleurs complémentaires.',
    title: 'Charts',
    variables: [
      { label: 'Chart 1', name: 'chart-1' },
      { label: 'Chart 2', name: 'chart-2' },
      { label: 'Chart 3', name: 'chart-3' },
      { label: 'Chart 4', name: 'chart-4' },
      { label: 'Chart 5', name: 'chart-5' },
    ],
  },
  {
    description: 'Tokens dédiés à la navigation latérale.',
    title: 'Sidebar',
    variables: [
      { label: 'Sidebar', name: 'sidebar' },
      { label: 'Sidebar foreground', name: 'sidebar-foreground' },
      { label: 'Sidebar primary', name: 'sidebar-primary' },
      { label: 'Sidebar primary foreground', name: 'sidebar-primary-foreground' },
      { label: 'Sidebar accent', name: 'sidebar-accent' },
      { label: 'Sidebar accent foreground', name: 'sidebar-accent-foreground' },
      { label: 'Sidebar border', name: 'sidebar-border' },
      { label: 'Sidebar ring', name: 'sidebar-ring' },
    ],
  },
] as const;

const defaultLightVariables: ThemeVariableMap = {
  'accent': 'oklch(0.967 0.001 286.375)',
  'accent-foreground': 'oklch(0.21 0.006 285.885)',
  'background': 'oklch(1 0 0)',
  'border': 'oklch(0.92 0.004 286.32)',
  'card': 'oklch(1 0 0)',
  'card-foreground': 'oklch(0.141 0.005 285.823)',
  'chart-1': 'oklch(0.785 0.115 274.713)',
  'chart-2': 'oklch(0.585 0.233 277.117)',
  'chart-3': 'oklch(0.511 0.262 276.966)',
  'chart-4': 'oklch(0.457 0.24 277.023)',
  'chart-5': 'oklch(0.398 0.195 277.366)',
  'destructive': 'oklch(0.577 0.245 27.325)',
  'foreground': 'oklch(0.141 0.005 285.823)',
  'input': 'oklch(0.92 0.004 286.32)',
  'muted': 'oklch(0.967 0.001 286.375)',
  'muted-foreground': 'oklch(0.552 0.016 285.938)',
  'popover': 'oklch(1 0 0)',
  'popover-foreground': 'oklch(0.141 0.005 285.823)',
  'primary': '#194697',
  'primary-foreground': '#ffffff',
  'ring': 'oklch(0.705 0.015 286.067)',
  'secondary': 'oklch(0.967 0.001 286.375)',
  'secondary-foreground': 'oklch(0.21 0.006 285.885)',
  'sidebar': 'oklch(0.985 0 0)',
  'sidebar-accent': 'oklch(0.967 0.001 286.375)',
  'sidebar-accent-foreground': 'oklch(0.21 0.006 285.885)',
  'sidebar-border': 'oklch(0.92 0.004 286.32)',
  'sidebar-foreground': 'oklch(0.141 0.005 285.823)',
  'sidebar-primary': '#194697',
  'sidebar-primary-foreground': '#ffffff',
  'sidebar-ring': 'oklch(0.705 0.015 286.067)',
};

const legacyDefaultDarkVariables: ThemeVariableMap = {
  'accent': 'oklch(0.274 0.006 286.033)',
  'accent-foreground': 'oklch(0.985 0 0)',
  'background': 'oklch(0.141 0.005 285.823)',
  'border': 'oklch(1 0 0 / 10%)',
  'card': 'oklch(0.21 0.006 285.885)',
  'card-foreground': 'oklch(0.985 0 0)',
  'chart-1': 'oklch(0.785 0.115 274.713)',
  'chart-2': 'oklch(0.585 0.233 277.117)',
  'chart-3': 'oklch(0.511 0.262 276.966)',
  'chart-4': 'oklch(0.457 0.24 277.023)',
  'chart-5': 'oklch(0.398 0.195 277.366)',
  'destructive': 'oklch(0.704 0.191 22.216)',
  'foreground': 'oklch(0.985 0 0)',
  'input': 'oklch(1 0 0 / 15%)',
  'muted': 'oklch(0.274 0.006 286.033)',
  'muted-foreground': 'oklch(0.705 0.015 286.067)',
  'popover': 'oklch(0.21 0.006 285.885)',
  'popover-foreground': 'oklch(0.985 0 0)',
  'primary': '#194697',
  'primary-foreground': '#ffffff',
  'ring': 'oklch(0.552 0.016 285.938)',
  'secondary': 'oklch(0.274 0.006 286.033)',
  'secondary-foreground': 'oklch(0.985 0 0)',
  'sidebar': 'oklch(0.21 0.006 285.885)',
  'sidebar-accent': 'oklch(0.274 0.006 286.033)',
  'sidebar-accent-foreground': 'oklch(0.985 0 0)',
  'sidebar-border': 'oklch(1 0 0 / 10%)',
  'sidebar-foreground': 'oklch(0.985 0 0)',
  'sidebar-primary': '#194697',
  'sidebar-primary-foreground': '#ffffff',
  'sidebar-ring': 'oklch(0.552 0.016 285.938)',
};

const previousDefaultDarkVariables: ThemeVariableMap = {
  'accent': 'oklch(0.255 0.004 286)',
  'accent-foreground': 'oklch(0.96 0.002 286)',
  'background': 'oklch(0.17 0.004 286)',
  'border': 'oklch(1 0 0 / 12%)',
  'card': 'oklch(0.215 0.004 286)',
  'card-foreground': 'oklch(0.96 0.002 286)',
  'chart-1': 'oklch(0.785 0.115 274.713)',
  'chart-2': 'oklch(0.585 0.233 277.117)',
  'chart-3': 'oklch(0.511 0.262 276.966)',
  'chart-4': 'oklch(0.457 0.24 277.023)',
  'chart-5': 'oklch(0.398 0.195 277.366)',
  'destructive': 'oklch(0.704 0.191 22.216)',
  'foreground': 'oklch(0.96 0.002 286)',
  'input': 'oklch(1 0 0 / 14%)',
  'muted': 'oklch(0.255 0.004 286)',
  'muted-foreground': 'oklch(0.72 0.01 286)',
  'popover': 'oklch(0.215 0.004 286)',
  'popover-foreground': 'oklch(0.96 0.002 286)',
  'primary': '#194697',
  'primary-foreground': '#ffffff',
  'ring': 'oklch(0.66 0.01 286)',
  'secondary': 'oklch(0.255 0.004 286)',
  'secondary-foreground': 'oklch(0.96 0.002 286)',
  'sidebar': 'oklch(0.205 0.004 286)',
  'sidebar-accent': 'oklch(0.255 0.004 286)',
  'sidebar-accent-foreground': 'oklch(0.96 0.002 286)',
  'sidebar-border': 'oklch(1 0 0 / 12%)',
  'sidebar-foreground': 'oklch(0.96 0.002 286)',
  'sidebar-primary': '#194697',
  'sidebar-primary-foreground': '#ffffff',
  'sidebar-ring': 'oklch(0.66 0.01 286)',
};

const defaultDarkVariables: ThemeVariableMap = {
  'accent': 'oklch(0.245 0.003 286)',
  'accent-foreground': 'oklch(0.96 0.002 286)',
  'background': 'oklch(0.185 0.003 286)',
  'border': 'oklch(1 0 0 / 10%)',
  'card': 'oklch(0.215 0.003 286)',
  'card-foreground': 'oklch(0.96 0.002 286)',
  'chart-1': 'oklch(0.785 0.115 274.713)',
  'chart-2': 'oklch(0.585 0.233 277.117)',
  'chart-3': 'oklch(0.511 0.262 276.966)',
  'chart-4': 'oklch(0.457 0.24 277.023)',
  'chart-5': 'oklch(0.398 0.195 277.366)',
  'destructive': 'oklch(0.704 0.191 22.216)',
  'foreground': 'oklch(0.96 0.002 286)',
  'input': 'oklch(0.245 0.003 286)',
  'muted': 'oklch(0.245 0.003 286)',
  'muted-foreground': 'oklch(0.72 0.01 286)',
  'popover': 'oklch(0.215 0.003 286)',
  'popover-foreground': 'oklch(0.96 0.002 286)',
  'primary': '#194697',
  'primary-foreground': '#ffffff',
  'ring': 'oklch(0.66 0.01 286)',
  'secondary': 'oklch(0.245 0.003 286)',
  'secondary-foreground': 'oklch(0.96 0.002 286)',
  'sidebar': 'oklch(0.215 0.003 286)',
  'sidebar-accent': 'oklch(0.245 0.003 286)',
  'sidebar-accent-foreground': 'oklch(0.96 0.002 286)',
  'sidebar-border': 'oklch(1 0 0 / 10%)',
  'sidebar-foreground': 'oklch(0.96 0.002 286)',
  'sidebar-primary': '#194697',
  'sidebar-primary-foreground': '#ffffff',
  'sidebar-ring': 'oklch(0.66 0.01 286)',
};

function migrateLegacyDarkVariables(variables?: Partial<ThemeVariableMap>) {
  if (!variables) {
    return defaultDarkVariables;
  }

  return Object.fromEntries(
    Object.entries(defaultDarkVariables).map(([key, value]) => {
      const nextValue = variables[key];

      if (typeof nextValue !== 'string') {
        return [key, value];
      }

      return [
        key,
        nextValue === legacyDefaultDarkVariables[key] || nextValue === previousDefaultDarkVariables[key]
          ? value
          : nextValue,
      ];
    }),
  ) as ThemeVariableMap;
}

const defaultThemeState: ThemeState = {
  darkVariables: defaultDarkVariables,
  lightVariables: defaultLightVariables,
  mode: 'light',
  radius: '0.625rem',
  surfaceRadius: '0.625rem',
};

const ThemeCustomizerContext = createContext<ThemeCustomizerContextValue | null>(null);

function readStoredThemeState() {
  if (typeof window === 'undefined') {
    return defaultThemeState;
  }

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);

    if (!raw) {
      return defaultThemeState;
    }

    const parsed = JSON.parse(raw) as Partial<ThemeState>;

    return {
      darkVariables: migrateLegacyDarkVariables(parsed.darkVariables),
      lightVariables: { ...defaultLightVariables, ...parsed.lightVariables },
      mode: parsed.mode === 'dark' || parsed.mode === 'system' ? parsed.mode : 'light',
      radius: typeof parsed.radius === 'string' ? parsed.radius : defaultThemeState.radius,
      surfaceRadius: typeof parsed.surfaceRadius === 'string'
        ? parsed.surfaceRadius
        : typeof parsed.radius === 'string'
          ? parsed.radius
          : defaultThemeState.surfaceRadius,
    } satisfies ThemeState;
  } catch {
    return defaultThemeState;
  }
}

function resolveMode(mode: ThemeMode) {
  if (typeof window === 'undefined') {
    return 'light';
  }

  if (mode !== 'system') {
    return mode;
  }

  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function applyThemeVariables(
  mode: Exclude<ThemeMode, 'system'>,
  radius: string,
  surfaceRadius: string,
  variables: ThemeVariableMap,
) {
  const root = document.documentElement;
  root.classList.toggle('dark', mode === 'dark');
  root.style.setProperty('--radius', radius);
  root.style.setProperty('--radius-surface', surfaceRadius);

  themeVariableNames.forEach((name) => {
    root.style.setProperty(`--${name}`, variables[name]);
  });
}

export function ThemeCustomizerProvider({ children }: PropsWithChildren) {
  const [themeState, setThemeState] = useState<ThemeState>(() => readStoredThemeState());
  const [systemMode, setSystemMode] = useState<'light' | 'dark'>(() => resolveMode('system'));

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const update = () => {
      setSystemMode(mediaQuery.matches ? 'dark' : 'light');
    };

    update();
    mediaQuery.addEventListener('change', update);

    return () => {
      mediaQuery.removeEventListener('change', update);
    };
  }, []);

  const effectiveMode = themeState.mode === 'system' ? systemMode : themeState.mode;
  const currentVariables = effectiveMode === 'dark' ? themeState.darkVariables : themeState.lightVariables;

  useEffect(() => {
    applyThemeVariables(effectiveMode, themeState.radius, themeState.surfaceRadius, currentVariables);
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(themeState));
  }, [currentVariables, effectiveMode, themeState]);

  const value = useMemo<ThemeCustomizerContextValue>(() => ({
    currentVariables,
    effectiveMode,
    mode: themeState.mode,
    radius: themeState.radius,
    surfaceRadius: themeState.surfaceRadius,
    resetTheme: () => {
      setThemeState(defaultThemeState);
    },
    setMode: (mode) => {
      setThemeState((current) => ({ ...current, mode }));
    },
    setRadius: (radius) => {
      setThemeState((current) => ({ ...current, radius }));
    },
    setSurfaceRadius: (surfaceRadius) => {
      setThemeState((current) => ({ ...current, surfaceRadius }));
    },
    setVariable: (name, value) => {
      setThemeState((current) => {
        if (current.mode === 'system') {
          return effectiveMode === 'dark'
            ? { ...current, darkVariables: { ...current.darkVariables, [name]: value } }
            : { ...current, lightVariables: { ...current.lightVariables, [name]: value } };
        }

        return current.mode === 'dark'
          ? { ...current, darkVariables: { ...current.darkVariables, [name]: value } }
          : { ...current, lightVariables: { ...current.lightVariables, [name]: value } };
      });
    },
  }), [currentVariables, effectiveMode, themeState]);

  return (
    <ThemeCustomizerContext.Provider value={value}>
      {children}
    </ThemeCustomizerContext.Provider>
  );
}

export function useThemeCustomizer() {
  const context = useContext(ThemeCustomizerContext);

  if (!context) {
    throw new Error('useThemeCustomizer must be used inside ThemeCustomizerProvider.');
  }

  return context;
}
